import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useScrollTo } from "@/lib/useScrollTo";
import { useLang } from "@/lib/i18n";

const HERO_IMG =
  "https://images.unsplash.com/photo-1757924461488-ef9ad0670978?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBpbnRlcmlvciUyMGRlc2lnbiUyMG1vZGVybnxlbnwwfHx8fDE3ODYwMDQxNDZ8MA&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay, k }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      key={k}
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.3, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const scrollTo = useScrollTo();
  const { copy, lang } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
        <img src={HERO_IMG} alt="Luxury interior by House of Alvirah" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-plum/70 via-plum/40 to-plum/75" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
        <motion.p
          data-testid="hero-overline"
          key={`ov-${lang}`}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="text-[11px] uppercase tracking-[0.45em] text-cream/80 mb-8 hero-shadow"
        >
          {copy.hero.overline}
        </motion.p>

        <h1 data-testid="hero-headline" className="font-serif text-cream text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight hero-shadow">
          <MaskedLine delay={0.35} k={`l1-${lang}`}>{copy.hero.line1}</MaskedLine>
          <MaskedLine delay={0.5} k={`l2-${lang}`}>
            <span className="italic text-cream">{copy.hero.line2Italic}</span> {copy.hero.line2}
          </MaskedLine>
        </h1>

        <motion.p
          key={`sub-${lang}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="mt-8 max-w-xl text-cream/90 text-sm md:text-base font-light leading-relaxed tracking-wide hero-shadow"
        >
          {copy.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-12 flex flex-col sm:flex-row items-center gap-4"
        >
          <button
            data-testid="hero-book-consultation-btn"
            onClick={() => scrollTo("#booking")}
            className="bg-gold text-cream text-[11px] uppercase tracking-[0.25em] px-10 py-4 hover:bg-cream hover:text-plum transition-colors duration-300"
          >
            {copy.hero.cta1}
          </button>
          <button
            data-testid="hero-quiz-btn"
            onClick={() => scrollTo("#quiz")}
            className="border border-cream/50 text-cream text-[11px] uppercase tracking-[0.25em] px-10 py-4 hover:border-cream hover:bg-cream/10 transition-colors duration-300"
          >
            {copy.hero.cta2}
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3"
      >
        <span className="text-[10px] uppercase tracking-[0.4em] text-cream/60">{copy.hero.scroll}</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="block w-px h-10 bg-gradient-to-b from-cream/80 to-transparent"
        />
      </motion.div>
    </section>
  );
};
